import React, { useState } from "react";
import { createRoot } from "react-dom/client";
import AnimateHeight from "react-animate-height";

import { SanitizedHTML } from "./SanitizedHTML";

// eslint-disable-next-line no-var
declare var expertiseListBlockSections: Array<sectionsInterface>;

const RESULTS_PER_PAGE = 5;
const COLLAPSE_ANIMATION_DURATION_MS = 500;

interface sectionsInterface {
  info_image_url: string;
  info_image_alt: string;
  info_title: string;
  info_description: string;
  details_title: string;
  details_sub_title: string;
  details_text: string;
  details_link_url: string;
  details_link_label: string;
  details_image_url: string;
  details_image_alt: string;
}

const Pagination = ({
  currentPage,
  setCurrentPage,
  openSection,
  setOpenSection,
  numItems,
  isDesktop = false,
}: {
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  openSection: number;
  setOpenSection: React.Dispatch<React.SetStateAction<number>>;
  numItems: number;
  isDesktop?: boolean;
}) => {
  const firstPage = 0;
  const lastPage = Math.floor(numItems / RESULTS_PER_PAGE);

  const openSectionPage = Math.floor(openSection / RESULTS_PER_PAGE);

  const decreaseCurrentPage = () => {
    if (currentPage > firstPage) {
      if (!isDesktop) {
        setOpenSection(-1);
      }
      setTimeout(
        () => {
          setCurrentPage(currentPage - 1);
        },
        !isDesktop && openSectionPage === currentPage
          ? COLLAPSE_ANIMATION_DURATION_MS
          : 0,
      );
    }
  };

  const increaseCurrentPage = () => {
    if (currentPage < lastPage) {
      if (!isDesktop) {
        setOpenSection(-1);
      }
      setTimeout(
        () => {
          setCurrentPage(currentPage + 1);
        },
        !isDesktop && openSectionPage === currentPage
          ? COLLAPSE_ANIMATION_DURATION_MS
          : 0,
      );
    }
  };
  if (numItems > 4) {
    return (
      <div className="arrows-wrapper">
        <div>
          <div
            onClick={decreaseCurrentPage}
            className={`arrow-back ${
              currentPage === firstPage ? "greyed-out" : ""
            }`}
          >
            <svg
              width="46"
              height="24"
              viewBox="0 0 46 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M0.939339 10.9393C0.353554 11.5251 0.353554 12.4749 0.939339 13.0607L10.4853 22.6066C11.0711 23.1924 12.0208 23.1924 12.6066 22.6066C13.1924 22.0208 13.1924 21.0711 12.6066 20.4853L4.12132 12L12.6066 3.51472C13.1924 2.92893 13.1924 1.97918 12.6066 1.3934C12.0208 0.807608 11.0711 0.807608 10.4853 1.3934L0.939339 10.9393ZM46 10.5L2 10.5L2 13.5L46 13.5L46 10.5Z" />
            </svg>
          </div>

          <svg
            className="line"
            width="1"
            height="26"
            viewBox="0 0 1 26"
            xmlns="http://www.w3.org/2000/svg"
          >
            <line
              x1="0.5"
              y1="-2.18557e-08"
              x2="0.500001"
              y2="26"
              stroke="#95928F"
            />
          </svg>

          <div
            onClick={increaseCurrentPage}
            className={`arrow-forward ${
              currentPage === lastPage ? "greyed-out" : ""
            }`}
          >
            <svg
              width="46"
              height="24"
              viewBox="0 0 46 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M45.0607 13.0607C45.6464 12.4749 45.6464 11.5251 45.0607 10.9393L35.5147 1.3934C34.9289 0.807611 33.9792 0.807611 33.3934 1.3934C32.8076 1.97919 32.8076 2.92893 33.3934 3.51472L41.8787 12L33.3934 20.4853C32.8076 21.0711 32.8076 22.0208 33.3934 22.6066C33.9792 23.1924 34.9289 23.1924 35.5147 22.6066L45.0607 13.0607ZM0 13.5H44V10.5H0V13.5Z" />
            </svg>
          </div>
        </div>

        {isDesktop && (
          <svg
            className="arrow-content-shape"
            width="40"
            height="70"
            viewBox="0 0 40 70"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5.46253e-06 70L0 39.0663C0.0191521 17.529 17.963 7.62939e-06 40 0L40 70L5.46253e-06 70Z"
              fill="#252E4B"
            />
          </svg>
        )}
      </div>
    );
  } else {
    return (
      <div className="arrows-wrapper no-arrows">
        {isDesktop && (
          <svg
            className="arrow-content-shape"
            width="40"
            height="70"
            viewBox="0 0 40 70"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5.46253e-06 70L0 39.0663C0.0191521 17.529 17.963 7.62939e-06 40 0L40 70L5.46253e-06 70Z"
              fill="#252E4B"
            />
          </svg>
        )}
      </div>
    );
  }
};

const SectionTitle = ({
  section,
  idx,
  openSection,
  setOpenSection,
  isDesktop,
}: {
  section: sectionsInterface;
  idx: number;
  openSection: number;
  setOpenSection: any;
  isDesktop?: boolean;
}) => {
  const onClickTitle = () => {
    if (idx !== openSection) {
      setOpenSection(idx);
    } else if (!isDesktop) {
      setOpenSection(-1);
    }
  };

  const isActive =
    idx === openSection || (isDesktop && openSection === -1 && idx === 0);

  return (
    <div className={isActive ? "active-section" : ""} onClick={onClickTitle}>
      <div
        className="leader border-bottom"
        style={{ boxSizing: "content-box" }}
      >
        <div className="leader-icon">
          <img src={section.info_image_url} alt={section.info_image_alt} />
        </div>
        <div className="leader-info">
          <p className="leader-name">{section.info_title}</p>
          <p className="leader-position">{section.info_description}</p>
        </div>
      </div>
    </div>
  );
};

const SectionInfo = ({ section }: { section: sectionsInterface }) => (
  <div className="two-parts-leader-about">
    <div>
      <p className="leader-name-black">{section.details_title}</p>
      <p
        className="leader-position"
        style={{ marginTop: "10px", marginBottom: "2em" }}
      >
        {section.details_sub_title}
      </p>
      <div className="leader-intro-text">
        <SanitizedHTML unsafeHTML={section.details_text} />
      </div>

      {section.details_link_url && (
        <div>
          <a href={section.details_link_url} className="button button-long">
            {section.details_link_label}
          </a>
        </div>
      )}
    </div>
    <div>
      <img
        src={section.details_image_url}
        className="leader-image"
        alt={section.details_image_alt}
      />
    </div>
  </div>
);

interface ExpertiseListBlockMobileProps {
  currentPageContent: Array<sectionsInterface>;
  startIndex: number;
  openSection: number;
  setOpenSection: React.Dispatch<React.SetStateAction<number>>;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  numItems: number;
}

const ExpertiseListBlockMobile = ({
  currentPageContent,
  startIndex,
  openSection,
  setOpenSection,
  currentPage,
  setCurrentPage,
  numItems,
}: ExpertiseListBlockMobileProps) => (
  <div id="elb-mobile">
    <div className="team-members-list">
      {currentPageContent.map((section: sectionsInterface, idx: number) => {
        const sectionIndex = idx + startIndex;
        return (
          <div key={sectionIndex}>
            <SectionTitle
              section={section}
              idx={sectionIndex}
              openSection={openSection}
              setOpenSection={setOpenSection}
            />
            <AnimateHeight
              duration={COLLAPSE_ANIMATION_DURATION_MS}
              height={openSection === sectionIndex ? "auto" : 0}
            >
              <SectionInfo section={section} />
            </AnimateHeight>
          </div>
        );
      })}
    </div>
    <Pagination
      currentPage={currentPage}
      setCurrentPage={setCurrentPage}
      openSection={openSection}
      setOpenSection={setOpenSection}
      numItems={numItems}
    />
  </div>
);

interface ExpertiseListBlockDesktopProps {
  sections: Array<sectionsInterface>;
  currentPageContent: Array<sectionsInterface>;
  startIndex: number;
  openSection: number;
  setOpenSection: React.Dispatch<React.SetStateAction<number>>;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  numItems: number;
}

const ExpertiseListBlockDesktop = ({
  sections,
  currentPageContent,
  startIndex,
  openSection,
  setOpenSection,
  currentPage,
  setCurrentPage,
  numItems,
}: ExpertiseListBlockDesktopProps) => (
  <div id="elb-desktop">
    <div className="leaders-grid">
      <div className="team-members-list">
        {currentPageContent.map((section: sectionsInterface, idx: number) => {
          const sectionIndex = idx + startIndex;
          return (
            <div key={sectionIndex}>
              <SectionTitle
                section={section}
                idx={sectionIndex}
                openSection={openSection}
                setOpenSection={setOpenSection}
                isDesktop={true}
              />
            </div>
          );
        })}
      </div>

      <Pagination
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        openSection={openSection}
        setOpenSection={setOpenSection}
        numItems={numItems}
        isDesktop={true}
      />
    </div>

    <div className="leader-about">
      <SectionInfo section={sections[openSection >= 0 ? openSection : 0]} />
    </div>
  </div>
);

const ExpertiseListBlock = ({
  sections,
}: {
  sections: Array<sectionsInterface>;
}) => {
  const defaultOpenSection = new URLSearchParams(document.location.search).get(
    "open",
  );
  let defaultOpenSectionIndex = -1;

  sections.forEach((section: sectionsInterface, idx: number) => {
    let titleFormatted = `${section.info_title}`
      .replace(" ", "_")
      .toLowerCase();
    if (titleFormatted === defaultOpenSection) {
      defaultOpenSectionIndex = idx;
    }
  });

  if (window.innerWidth > 915 && defaultOpenSectionIndex == -1) {
    defaultOpenSectionIndex = 0;
  }

  const [openSection, setOpenSection] = useState(defaultOpenSectionIndex);
  const [currentPage, setCurrentPage] = useState(
    defaultOpenSectionIndex !== -1
      ? Math.floor(openSection / RESULTS_PER_PAGE)
      : 0,
  );
  const startIndex = currentPage * RESULTS_PER_PAGE;
  const endIndex = (currentPage + 1) * RESULTS_PER_PAGE;

  const currentPageContent = sections.slice(startIndex, endIndex);

  return (
    <>
      <ExpertiseListBlockMobile
        currentPageContent={currentPageContent}
        startIndex={startIndex}
        openSection={openSection}
        setOpenSection={setOpenSection}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        numItems={sections.length - 1}
      />
      <ExpertiseListBlockDesktop
        sections={sections}
        currentPageContent={currentPageContent}
        startIndex={startIndex}
        openSection={openSection}
        setOpenSection={setOpenSection}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        numItems={sections.length - 1}
      />
    </>
  );
};

createRoot(document.getElementById("expertise-list-block-content")!).render(
  <ExpertiseListBlock sections={expertiseListBlockSections} />,
);
